import { Box, Image, Tooltip } from "@chakra-ui/react";
import { randomColor } from "@chakra-ui/theme-tools";
import React from "react";
import { HiOutlineClipboardList } from "react-icons/hi";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCog8Tooth,
  HiOutlineCurrencyDollar,
  HiOutlineMagnifyingGlass,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";
import { matchPath, useLocation } from "react-router-dom";

import { useAdvancedCustomTemplatesEnabled } from "../../hooks/useAdvancedCustomTemplatesEnabled";
import { useAiInterviewerEnabled } from "../../hooks/useAiInterviewerEnabled";
import { useOrgAccessMap } from "../../hooks/useOrgFeatureAccess";
import {
  AiInterviewerUserRoleName,
  CurrentUserFragment,
  FeatureName,
} from "../../main/graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../main/graphql/hooks/useFeatureFlag";
import { CurrentPlanUserFragment } from "../../plan/graphql";
import { asArray } from "../../utils/array";
import {
  canManageTraining,
  canViewAdvancedInsights,
  canViewIQReport,
  canViewOrgInsights,
} from "../../utils/permissions";
import { IconButton } from "../Buttons";
import { AiChatIcon, GradCapIcon, InsightsIcon } from "../Icons";
import { NavItem, SubNavItem } from "./SidebarNavItem";

type NavItemsParams = {
  app: "main" | "plan";
  enabledProducts: Array<"interview" | "plan">;
  currentUser?: CurrentUserFragment | CurrentPlanUserFragment;
  onClick?(navItem: NavItem | SubNavItem): void;
  onSignOut?(): void;
};

const useIsActive = (): ((paths: string | string[]) => boolean) => {
  const location = useLocation();

  return (paths) => {
    return asArray(paths).some((path) => matchPath(path, location.pathname));
  };
};

/**
 * Prepares the items that will go into page navigation for the current user
 */
export const useNavItems = ({
  app,
  enabledProducts,
  currentUser,
  onClick,
  onSignOut,
}: NavItemsParams): [NavItem[], NavItem[]] => {
  const isActive = useIsActive();
  const myInsightsDisabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "analytics:my-insights:disabled"
  );
  const iqReportFlagEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "analytics:quality-report"
  );
  const topicTrendsEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "analytics:topic-trends"
  );
  const candidateMotivationsEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser(
      "analytics:candidate-motivations"
    );
  const candidateQuestionsEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "analytics:candidate-questions"
  );
  const skillsReportEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "analytics:skills-report"
  );
  const callAiSummaryTemplatesEnabled =
    useAdvancedCustomTemplatesEnabled(currentUser);
  const screenFlagEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser("ai_interviewer:v1");
  const aiInterviewersEnabled = useAiInterviewerEnabled();
  const allInterviewsEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser("all_interviews");
  const insightsChatEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "insights:advanced-chat"
  );

  const accessMap = useOrgAccessMap(currentUser);

  if (!currentUser) return [[], []];

  const canAccessPrivateCalls =
    currentUser.userRole?.canAccessPrivateCalls ?? false;

  // Search
  const search: NavItem = {
    icon: HiOutlineMagnifyingGlass,
    label: "Search",
    isExternal: app !== "main",
    to: app !== "main" ? "/search" : undefined,
  };

  // Plan
  const planEnabled = enabledProducts.includes("plan");
  const plan: NavItem = {
    icon: HiOutlineClipboardList,
    label: "Plan",
    isActive: isActive(["/plan", "/plan/*"]),
    to: planEnabled ? "/plan/job-descriptions" : "/plan/upgrade",
    enabled: planEnabled,
    isExternal: app !== "plan",
  };

  // Interview
  const interview: NavItem = {
    icon: HiOutlineChatBubbleLeftRight,
    label: "Interview",
    enabled: enabledProducts.includes("interview"),
  };
  if (interview.enabled) {
    interview.subItems = [];
    interview.subItems.push(
      {
        label: "Recordings",
        to: "/search",
        isExternal: app === "plan",
        isActive: isActive(["/search", "/interview/*"]),
      },
      {
        label: "Candidates",
        to: "/candidates",
        isExternal: app === "plan",
        isActive: isActive(["/candidates", "/candidate/:id/*"]),
      },
      {
        label: "Positions",
        to: "/positions",
        isExternal: app === "plan",
        isActive: isActive(["/positions/*", "/position/:id/*"]),
      },
      {
        label: "Interview Guides",
        to: "/guides",
        isExternal: app === "plan",
        isActive: isActive(["/guides/*", "/guide/:id/*"]),
      },
      {
        label: "Clips",
        to: "/clips",
        isExternal: app === "plan",
        isActive: isActive("/clips"),
      },
      {
        label: "Playlists",
        to: "/playlists",
        isExternal: app === "plan",
        isActive: isActive(["/playlists/*", "/playlist/:id"]),
      }
    );
    if (callAiSummaryTemplatesEnabled) {
      interview.subItems.push({
        label: "AI Notes Templates",
        to: "/templates",
        isExternal: app === "plan",
        isActive: isActive("/templates"),
      });
    }
    if (canAccessPrivateCalls && allInterviewsEnabled) {
      const positionsIndex = interview.subItems.findIndex(
        (item) => item.label === "Positions"
      );
      interview.subItems.splice(positionsIndex + 1, 0, {
        label: "Scheduled Interviews",
        to: "/interviews",
        isExternal: app === "plan",
        isActive: isActive(["/interviews"]),
      });
    }
  } else {
    interview.to = "/upgrade";
    interview.isActive = isActive(interview.to);
    interview.isExternal = app !== "main";
  }

  // Training
  const trainingManage: NavItem = {
    icon: GradCapIcon,
    label: "Train",
    isExternal: app === "plan",
    enabled: accessMap?.get(FeatureName.Training)?.isVisible,
    to: accessMap?.get(FeatureName.Training)?.marketingRedirect
      ? "/training/upgrade"
      : undefined,
    subItems: [
      {
        label: "Manage Programs",
        to: "/training/manage",
        isExternal: app === "plan",
        isActive:
          isActive(["/training/manage", "/training/manage/:id"]) &&
          !isActive("/training/manage/all-trainees"),
      },
      {
        label: "All Trainees",
        to: "/training/manage/all-trainees",
        isExternal: app === "plan",
        isActive: isActive("/training/manage/all-trainees"),
      },
      {
        label: "My Training",
        to: "/training/view",
        isExternal: app === "plan",
        isActive: isActive("/training/view/*"),
      },
    ],
  };
  const trainingView: NavItem = {
    icon: GradCapIcon,
    label: "Train",
    isActive: isActive("/training/*"),
    enabled: accessMap?.get(FeatureName.Training)?.isVisible,
    to: accessMap?.get(FeatureName.Training)?.marketingRedirect
      ? "/training/upgrade"
      : "/training/view",
    isExternal: app === "plan",
  };
  const training = canManageTraining(currentUser)
    ? trainingManage
    : trainingView;

  const mainNavItems = [search, plan, interview, training];

  // The RBAC for Screen/AI Interviewers isn't fully implemented, so we're using these rules:
  // 1. If org flag is OFF: Show nav item with upgrade tag (for everyone)
  // 2. If org flag is ON: Show nav item (no upgrade tag) ONLY to users who can access it
  //    - Users without access don't see the link at all (to avoid 404 bad UX)
  // 3. If org has Screen enabled AND user is Screen ADMIN: Show nav item regardless of other logic
  const isScreenAdmin =
    "aiInterviewerUserRole" in currentUser &&
    currentUser.aiInterviewerUserRole?.name === AiInterviewerUserRoleName.Admin;
  const showScreenLink =
    !screenFlagEnabled ||
    aiInterviewersEnabled ||
    (screenFlagEnabled && isScreenAdmin);
  if (showScreenLink) {
    mainNavItems.splice(mainNavItems.length - 1, 0, {
      icon: AiChatIcon,
      label: "Screen",
      to: screenFlagEnabled ? "/ai-interviewers" : "/ai-interviewer/upgrade",
      isActive: isActive(["/ai-interviewer/*", "/ai-interviewers/*"]),
      isExternal: app === "plan",
      enabled: screenFlagEnabled,
    });
  }

  // Insights
  const iqReportEnabled = iqReportFlagEnabled && canViewIQReport(currentUser);
  const currentUserHasAdvancedInsightsPermissions =
    canViewAdvancedInsights(currentUser) &&
    (topicTrendsEnabled ||
      candidateMotivationsEnabled ||
      skillsReportEnabled ||
      candidateQuestionsEnabled);
  const showAdvancedInsightsRedirect =
    currentUserHasAdvancedInsightsPermissions &&
    accessMap?.get(FeatureName.AdvancedInsights)?.marketingRedirect;
  const showAdvancedInsights =
    currentUserHasAdvancedInsightsPermissions &&
    accessMap?.get(FeatureName.AdvancedInsights)?.isVisible;
  const insightsSubItems: SubNavItem[] = [
    {
      label: "Overview",
      to: accessMap?.get(FeatureName.Insights)?.marketingRedirect
        ? "/insights/upgrade"
        : "/insights",
      isExternal: app === "plan",
      isActive: isActive("/insights/overview"),
      enabled: accessMap?.get(FeatureName.Insights)?.isVisible,
    },
    {
      label: "Reports",
      to: accessMap?.get(FeatureName.Insights)?.marketingRedirect
        ? "/insights/upgrade"
        : iqReportEnabled
        ? "/insights/report"
        : "/insights/total_interviews",
      isExternal: app === "plan",
      isActive: isActive("/insights/report"),
      enabled: accessMap?.get(FeatureName.Insights)?.isVisible,
    },
  ];

  if (showAdvancedInsights || showAdvancedInsightsRedirect) {
    let defaultRoute;
    if (showAdvancedInsightsRedirect) {
      defaultRoute = "upgrade";
    } else if (candidateMotivationsEnabled) {
      defaultRoute = "candidate-motivations";
    } else if (candidateQuestionsEnabled) {
      defaultRoute = "candidate-questions";
    } else if (skillsReportEnabled) {
      defaultRoute = "skill-report/all";
    } else {
      defaultRoute = "topic-trends";
    }

    insightsSubItems.push({
      label: "Advanced Insights",
      to: `/insights/${defaultRoute}`,
      isExternal: app === "plan",
      enabled: showAdvancedInsights,
      isActive:
        isActive(`/insights/candidate-motivations`) ||
        isActive(`/insights/topic-trends`) ||
        isActive(`/insights/candidate-questions`) ||
        isActive(`/insights/skill-report`),
    });
  }

  if (insightsChatEnabled) {
    insightsSubItems.push({
      label: "Custom Insights",
      to: `/insights/custom`,
      isExternal: app === "plan",
      isActive: isActive(`/insights/custom`),
    });
  }

  if (!myInsightsDisabled) {
    insightsSubItems.splice(1, 0, {
      label: "My Insights",
      to: `/insights/interviewer/${currentUser.id}`,
      isExternal: app === "plan",
      isActive: isActive("/insights/interviewer/*"),
    });
  }

  const insights: NavItem = {
    icon: InsightsIcon,
    label: "Insights",
    subItems: insightsSubItems,
  };

  if (canViewOrgInsights(currentUser)) {
    mainNavItems.push(insights);
  } else if (!myInsightsDisabled) {
    mainNavItems.push({
      icon: InsightsIcon,
      label: "My Insights",
      isActive: isActive("/insights/my-insights"),
      to: `/insights/interviewer/${currentUser.id}`,
      isExternal: app === "plan",
    });
  }

  const help: NavItem = {
    icon: HiOutlineQuestionMarkCircle,
    label: "Help",
    to: "/support",
    isActive: isActive("/support"),
    isExternal: app === "plan",
  };

  const settings: NavItem = {
    icon: HiOutlineCog8Tooth,
    label: "Configure",
    to: "/settings/user",
    isExternal: app === "plan",
    isActive: isActive("/settings/*"),
  };

  const referral: NavItem = {
    icon: HiOutlineCurrencyDollar,
    label: "Refer & Earn",
    to: "https://brighthire.notion.site/BrightHire-Customer-Referral-Program-21d3e9f8ae808074b0addaf522fb1d09?pvs=74",
    isExternal: true,
    target: "_blank",
    isActive: false,
  };

  const user: NavItem = {
    icon: (props) => {
      const fallback = (
        <Box
          borderRadius="full"
          bg={randomColor({ string: currentUser.fullName })}
          boxSize="6"
          fontSize="xs"
          border="1px solid white"
          {...props}
        >
          <Box
            display="flex"
            h="100%"
            alignItems="center"
            justifyContent="center"
          >
            {getInitials(currentUser)}
          </Box>
        </Box>
      );

      if (!currentUser.profilePicUrl) {
        return fallback;
      }

      return (
        <Image
          src={currentUser.profilePicUrl}
          fallback={fallback}
          fallbackStrategy="onError"
          borderRadius="full"
          border="1px solid white"
          {...props}
        />
      );
    },
    label: currentUser.fullName,
    id: "nav-item-user",
    static: true,
    endSlot: onSignOut && (
      <Tooltip
        label={currentUser.isImpersonated ? "Un-impersonate" : "Sign out"}
        openDelay={500}
        placement="left"
      >
        <IconButton
          aria-label="Sign out"
          icon={<HiOutlineArrowRightOnRectangle size="24" />}
          bg="transparent"
          _hover={{ bg: "whiteAlpha.400" }}
          _active={{ bg: "whiteAlpha.300" }}
          minW="unset"
          boxSize="10"
          p="1"
          onClick={onSignOut}
        />
      </Tooltip>
    ),
  };

  const addClickHandler = ({ subItems, ...navItem }: NavItem): NavItem => ({
    ...navItem,
    onClick: () => onClick?.(navItem),
    subItems: subItems?.map((subItem) => ({
      ...subItem,
      onClick: () => onClick?.(subItem),
    })),
  });

  const footerNavItems = [help, settings, referral, user];

  return [
    mainNavItems.map(addClickHandler),
    footerNavItems.map(addClickHandler),
  ];
};

const getInitials = (
  user: CurrentUserFragment | CurrentPlanUserFragment
): string => {
  const names = user.fullName.split(" ");
  if (names.length > 1) {
    return names[0].charAt(0) + names[names.length - 1].charAt(0);
  }
  return names[0].charAt(0);
};
